/** Skripsi team assignment + dosen confirmation. */

import { createDoc, updateDoc, getById } from '../service';
import { COL } from '../../collections';
import { utcNowIso } from '../../format';
import { notifyRoleUser } from './notify';
import type { DosenSubRole, SkripsiDoc, SkripsiTeamDoc } from '../../types';

/**
 * Assign (or replace) a dosen on a skripsi team. Replacing an existing active
 * assignment archives it (status -> inactive, endedAt set) — history preserved,
 * access revoked.
 */
export async function assignTeamMember(input: {
  skripsiId: string;
  dosenId: string;
  role: Exclude<DosenSubRole, 'pembimbing-akademik'>;
  assignedBy: string;
  previousTeamId?: string;
  note?: string;
  /** Dosen outside the skripsi's prodi gets a special external mark. */
  external?: boolean;
}) {
  const now = utcNowIso();

  if (input.previousTeamId) {
    const prev = await getById<SkripsiTeamDoc>(COL.skripsiTeam, input.previousTeamId);
    if (prev && prev.status === 'active') {
      const historyEntry = {
        role: prev.role,
        status: 'inactive' as const,
        assignedAt: prev.assignedAt,
        endedAt: now,
        note: `Digantikan · ${input.note ?? ''}`.trim(),
      };
      await updateDoc<SkripsiTeamDoc>(COL.skripsiTeam, prev.id, {
        status: 'inactive',
        endedAt: now,
        note: `Digantikan · ${input.note ?? ''}`.trim(),
        history: [...(prev.history ?? []), historyEntry],
      });
    }
  }

  const skripsi = await getById<SkripsiDoc>(COL.skripsi, input.skripsiId);
  await createDoc<SkripsiTeamDoc>(COL.skripsiTeam, {
    skripsiId: input.skripsiId,
    dosenId: input.dosenId,
    role: input.role,
    status: 'active',
    assignedAt: now,
    assignedBy: input.assignedBy,
    note: input.note,
    external: input.external,
    konfirmasi: { status: 'pending', at: now },
    history: [{ role: input.role, status: 'active', assignedAt: now, note: input.note }],
  });

  // Penugasan disampaikan melalui sistem (SOP II.12): notify the assigned dosen
  // with a link to the skripsi so they can confirm or raise an objection.
  await notifyRoleUser(input.dosenId, 'dosen', {
    title: `Penugasan sebagai ${input.role === 'pembimbing-1' ? 'Pembimbing I' : input.role === 'pembimbing-2' ? 'Pembimbing II' : 'Tim penguji'}`,
    body: skripsi
      ? `Anda ditugaskan pada skripsi “${skripsi.judul}”. Silakan konfirmasi penugasan.`
      : 'Anda ditugaskan pada sebuah skripsi. Silakan konfirmasi penugasan.',
    type: 'info',
    link: `skripsi/${input.skripsiId}`,
  });
  // The mahasiswa sees the new assignment on the skripsi detail timeline.
  if (skripsi) {
    await notifyRoleUser(skripsi.mahasiswaId, 'mahasiswa', {
      title: 'Tim pembimbing diperbarui',
      body: `Susunan tim pada skripsi “${skripsi.judul}” berubah. Silakan periksa tahap penetapan pembimbing.`,
      type: 'info',
      link: `skripsi/${input.skripsiId}`,
    });
  }

  return { skripsiId: input.skripsiId, dosenId: input.dosenId };
}

/** Dosen confirms (or objects to) an assignment — SOP penetapan pembimbing step 13/14. */
export async function setPembimbingKonfirmasi(input: {
  teamId: string;
  status: 'diterima' | 'menolak';
  note?: string;
  actorId: string;
  actorName?: string;
}) {
  const team = await getById<SkripsiTeamDoc>(COL.skripsiTeam, input.teamId);
  if (!team || team.dosenId !== input.actorId) return;
  const now = utcNowIso();
  return updateDoc<SkripsiTeamDoc>(COL.skripsiTeam, input.teamId, {
    konfirmasi: { status: input.status, at: now, note: input.note },
  });
}
